import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { loadMercadoPago } from "@mercadopago/sdk-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CreditCard, Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MpCardDialog — Checkout Transparente de CARTÃO via Mercado Pago (MP.js CardForm iframe).
 * Props:
 *  - open, onOpenChange(bool)
 *  - plan: { id, name, price, max_installments }
 *  - createPayload: dados extras a enviar (student_id | pending_student | payer_email ...)
 *  - endpoints: { create, status, sync }
 *  - auth?: axios instance autenticada (opcional)
 *  - payerEmail?: string
 *  - payerCpf?: string
 *  - onPaid(chargeInfo): callback após baixa confirmada
 */
export default function MpCardDialog({
  open, onOpenChange, plan, createPayload, endpoints, auth, payerEmail = "", payerCpf = "", onPaid,
  title = "Pagar com cartão — Mercado Pago",
}) {
  const api = auth || axios.create({ baseURL: API });
  const ep = endpoints || {
    create: "/payments/mp/card/create",
    status: "/portal/mp/card/status/",
    sync: "/portal/mp/card/sync/",
  };

  const [sdkReady, setSdkReady] = useState(false);
  const [publicKey, setPublicKey] = useState(null);
  const [initError, setInitError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [charge, setCharge] = useState(null);
  const [pendingPoll, setPendingPoll] = useState(false);

  const cardFormRef = useRef(null);
  const pollRef = useRef(null);
  const amount = Number(plan?.price || 0);
  const maxInstallments = Math.max(1, Math.min(24, Number(plan?.max_installments || 1)));

  // 1. Load Public Key from backend when dialog opens
  useEffect(() => {
    if (!open) return;
    setInitError(null);
    setCharge(null);
    setProcessing(false);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payments/mp/public-key`);
        setPublicKey(data.public_key);
      } catch (e) {
        setInitError("Mercado Pago (cartão) não está configurado no servidor. Peça ao administrador para cadastrar a Public Key.");
      }
    })();
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      try { cardFormRef.current?.unmount?.(); } catch {}
      cardFormRef.current = null;
    };
    // eslint-disable-next-line
  }, [open]);

  // 2. Mount CardForm once SDK + Public Key are ready
  useEffect(() => {
    if (!open || !publicKey || !plan || charge) return;
    let cancelled = false;
    (async () => {
      try {
        await loadMercadoPago();
        if (cancelled || !window.MercadoPago) return;
        const mp = new window.MercadoPago(publicKey, { locale: "pt-BR" });
        const _fieldStyle = { color: "#ffffff", fontSize: "14px", placeholderColor: "#94a3b8", lineHeight: "1.4" };
        cardFormRef.current = mp.cardForm({
          amount: amount.toFixed(2),
          iframe: true,
          form: {
            id: "mp-card-form",
            cardNumber: { id: "mp-card-number", placeholder: "0000 0000 0000 0000", style: _fieldStyle },
            expirationDate: { id: "mp-expiration", placeholder: "MM/AA", style: _fieldStyle },
            securityCode: { id: "mp-cvv", placeholder: "CVV", style: _fieldStyle },
            cardholderName: { id: "mp-cardholder-name", placeholder: "Nome impresso no cartão" },
            issuer: { id: "mp-issuer", placeholder: "Banco emissor" },
            installments: { id: "mp-installments", placeholder: "Parcelas" },
            identificationType: { id: "mp-id-type", placeholder: "CPF" },
            identificationNumber: { id: "mp-id-number", placeholder: "CPF" },
            cardholderEmail: { id: "mp-email", placeholder: "E-mail" },
          },
          callbacks: {
            onInstallmentsReceived: () => {
              setTimeout(() => {
                const sel = document.getElementById("mp-installments");
                if (!sel) return;
                [...sel.options].forEach(o => { if (o.value && Number(o.value) > maxInstallments) o.remove(); });
                if (maxInstallments === 1 && [...sel.options].some(o => o.value === "1")) sel.value = "1";
              }, 0);
            },
            onFormMounted: (error) => {
              if (error) { setInitError("Falha ao carregar o formulário do Mercado Pago."); return; }
              setSdkReady(true);
            },
            onSubmit: async (event) => {
              event.preventDefault();
              setProcessing(true);
              try {
                const d = cardFormRef.current.getCardFormData();
                if (!d.token) throw new Error("Não foi possível validar o cartão. Confira os dados.");
                const installments = Number(d.installments);
                if (!installments || installments < 1 || installments > maxInstallments) {
                  throw new Error(`Parcelamento permitido para este plano: 1x a ${maxInstallments}x`);
                }
                const body = {
                  ...createPayload,
                  token: d.token,
                  payment_method_id: d.paymentMethodId,
                  issuer_id: d.issuerId ? Number(d.issuerId) : undefined,
                  installments,
                  payer: {
                    email: d.cardholderEmail || payerEmail,
                    identification: {
                      type: (d.identificationType || "CPF").toUpperCase(),
                      number: (d.identificationNumber || payerCpf || "").replace(/\D/g, ""),
                    },
                  },
                };
                const { data } = await api.post(ep.create, body);
                setCharge(data);
                if (data.status === "paid") {
                  toast.success("Pagamento aprovado!");
                  onPaid && onPaid({ ...data, status: "paid" });
                  onOpenChange(false);
                } else if (data.status === "rejected") {
                  toast.error(data.message || "Pagamento recusado pelo emissor.");
                } else {
                  // pending / in_process → polling
                  setPendingPoll(true);
                  pollRef.current = setInterval(async () => {
                    try {
                      const { data: st } = await api.get(`${ep.status}${data.external_reference}`);
                      if (st.status === "paid") {
                        clearInterval(pollRef.current); pollRef.current = null;
                        setCharge((c) => c ? { ...c, status: "paid" } : null);
                        toast.success("Pagamento confirmado!");
                        onPaid && onPaid({ ...data, status: "paid" });
                        onOpenChange(false);
                      } else if (st.status === "rejected") {
                        clearInterval(pollRef.current); pollRef.current = null;
                        setCharge((c) => c ? { ...c, status: "rejected", mp_status_detail: st.mp_status_detail } : null);
                      }
                    } catch (e) { /* ignora */ }
                  }, 4000);
                }
              } catch (e) {
                toast.error(e.response?.data?.detail || e.message || "Erro ao processar cartão");
              } finally { setProcessing(false); }
            },
          },
        });
      } catch (e) {
        setInitError("Falha ao carregar o SDK do Mercado Pago. Verifique a conexão.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [open, publicKey, amount, maxInstallments, charge]);

  const forceSync = async () => {
    if (!charge?.external_reference) return;
    try {
      const { data } = await api.post(`${ep.sync}${charge.external_reference}`);
      if (data.status === "paid") {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        onPaid && onPaid({ ...charge, status: "paid" });
        onOpenChange(false);
      }
    } catch (e) { /* ignora */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mp-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-600"/>{title}
          </DialogTitle>
        </DialogHeader>

        {initError && (
          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
            {initError}
          </div>
        )}

        {plan && !charge && (
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Valor</div>
            <div className="font-heading text-3xl font-black text-emerald-700">R$ {amount.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-0.5">{plan.name} · até <strong>{maxInstallments}x</strong> no cartão</div>
          </div>
        )}

        {charge?.status === "rejected" && (
          <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-center" data-testid="mp-card-rejected">
            <XCircle className="w-10 h-10 text-rose-600 mx-auto mb-2"/>
            <div className="font-bold text-rose-700">Pagamento recusado</div>
            <div className="text-xs text-slate-600 mt-1">{charge.message || charge.mp_status_detail || "Tente outro cartão."}</div>
            <Button variant="outline" className="mt-3 w-full" onClick={() => { setCharge(null); setSdkReady(false); }} data-testid="mp-card-retry">
              Tentar outro cartão
            </Button>
          </div>
        )}

        {charge?.status === "pending" && pendingPoll && (
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-center" data-testid="mp-card-pending">
            <Loader2 className="w-10 h-10 text-amber-600 animate-spin mx-auto mb-2"/>
            <div className="font-bold text-amber-800">Pagamento em análise</div>
            <div className="text-xs text-slate-600 mt-1">Aguardando confirmação do Mercado Pago…</div>
            <Button variant="outline" className="mt-3 w-full" onClick={forceSync} data-testid="mp-card-sync">
              Já paguei — verificar agora
            </Button>
          </div>
        )}

        {charge?.status === "paid" && (
          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-center" data-testid="mp-card-paid">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2"/>
            <div className="font-bold text-emerald-700">Pagamento aprovado!</div>
          </div>
        )}

        {!charge && (
          <form id="mp-card-form" className="space-y-3 mt-2" data-testid="mp-card-form">
            <div>
              <Label>Número do cartão</Label>
              <div id="mp-card-number" className="h-10 rounded-md border px-3 py-2 mp-field"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Validade</Label>
                <div id="mp-expiration" className="h-10 rounded-md border px-3 py-2 mp-field"/>
              </div>
              <div>
                <Label>CVV</Label>
                <div id="mp-cvv" className="h-10 rounded-md border px-3 py-2 mp-field"/>
              </div>
            </div>
            <div>
              <Label>Nome impresso no cartão</Label>
              <Input id="mp-cardholder-name" placeholder="Como está no cartão" data-testid="mp-card-cardholder" className="mp-field"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Banco emissor</Label>
                <select id="mp-issuer" className="h-10 w-full rounded-md border px-2 mp-field text-sm"/>
              </div>
              <div>
                <Label>Parcelas</Label>
                <select id="mp-installments" className={`h-10 w-full rounded-md border px-2 mp-field text-sm ${maxInstallments === 1 ? "hidden" : ""}`} data-testid="mp-card-installments"/>
                {maxInstallments === 1 && <div className="h-10 flex items-center px-3 rounded-md border text-sm font-semibold bg-slate-50" data-testid="mp-card-installments-fixed">1x (à vista)</div>}
              </div>
            </div>
            <div className="grid grid-cols-[90px_1fr] gap-3">
              <div>
                <Label>Doc.</Label>
                <select id="mp-id-type" className="h-10 w-full rounded-md border px-2 mp-field text-sm"/>
              </div>
              <div>
                <Label>Número</Label>
                <Input id="mp-id-number" defaultValue={payerCpf?.replace(/\D/g, "")} placeholder="CPF/CNPJ" data-testid="mp-card-id-number" className="mp-field"/>
              </div>
            </div>
            <div>
              <Label>E-mail</Label>
              <Input id="mp-email" type="email" defaultValue={payerEmail} placeholder="voce@email.com" data-testid="mp-card-email" className="mp-field"/>
            </div>

            <Button type="submit" disabled={!sdkReady || processing} className="w-full bg-emerald-600 hover:bg-emerald-700 font-black uppercase mt-2" data-testid="mp-card-submit">
              {processing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Processando…</>) : (<><ShieldCheck className="w-4 h-4 mr-2"/>Pagar com cartão</>)}
            </Button>
            <p className="text-[10px] text-slate-400 text-center">Processamento seguro Mercado Pago. Seu cartão não é salvo no nosso servidor.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
